import { Injectable, inject } from '@angular/core';
import {
  Action,
  NgxsOnInit,
  Selector,
  State,
  StateContext,
  StateToken,
  Store,
  createSelector,
} from '@ngxs/store';
import { ActivitiesGateway } from '@wilson/api/gateway';
import { AuthState } from '@wilson/auth/core';
import { ActivityCategory } from '@wilson/interfaces';
import { EntityLangType, LocalizationState } from '@wilson/localization';
import { handleWithRetryAndError } from '@wilson/rxjs/shared';
import { Observable, filter, take, tap } from 'rxjs';
import { FetchActivityCategories } from './activity-categories.actions';

export interface ActivityCategoriesStateModel {
  categories: ActivityCategory[];
}

const defaults: ActivityCategoriesStateModel = {
  categories: [],
};

export const ACTIVITY_CATEGORIES_STATE_TOKEN =
  new StateToken<ActivityCategoriesStateModel>('activityCategories');

@State<ActivityCategoriesStateModel>({
  name: ACTIVITY_CATEGORIES_STATE_TOKEN,
  defaults,
})
@Injectable()
export class ActivityCategoriesState implements NgxsOnInit {
  private readonly activitiesGateway = inject(ActivitiesGateway);
  private readonly store = inject(Store);

  @Selector()
  static categories(state: ActivityCategoriesStateModel): ActivityCategory[] {
    return state.categories;
  }

  @Selector()
  static enabledCategories(
    state: ActivityCategoriesStateModel,
  ): ActivityCategory[] {
    return state.categories.filter((category) => category.isEnabled);
  }

  static enabledCategoriesSortedByName = createSelector(
    [ActivityCategoriesState, LocalizationState.getEntityLang],
    (state: ActivityCategoriesStateModel, entityLang: EntityLangType) =>
      state.categories
        .filter((category) => category.isEnabled)
        .sort((a, b) =>
          (a[entityLang] || a.nameEn || '').localeCompare(
            b[entityLang] || b.nameEn || '',
          ),
        ),
  );

  /**
   * Enabled categories plus the currently selected one when it was later
   * disabled in company settings, so existing selections still render.
   */
  static selectableCategoriesSortedByName(selectedCategoryId?: string | null) {
    return createSelector(
      [ActivityCategoriesState, LocalizationState.getEntityLang],
      (state: ActivityCategoriesStateModel, entityLang: EntityLangType) =>
        state.categories
          .filter(
            (category) =>
              category.isEnabled ||
              (!!selectedCategoryId && category.id === selectedCategoryId),
          )
          .sort((a, b) =>
            (a[entityLang] || a.nameEn || '').localeCompare(
              b[entityLang] || b.nameEn || '',
            ),
          ),
    );
  }

  static categoryById(
    id: string,
  ): (state: ActivityCategoriesStateModel) => ActivityCategory | undefined {
    return createSelector([ActivityCategoriesState], (state) =>
      state.categories.find((category) => category.id === id),
    );
  }

  static categoryByName(
    name: string,
  ): (state: ActivityCategoriesStateModel) => ActivityCategory | undefined {
    return createSelector([ActivityCategoriesState], (state) =>
      state.categories.find((category) => category.name === name),
    );
  }

  static categoryByIdExists(
    id: string,
  ): (state: ActivityCategoriesStateModel) => boolean {
    return createSelector([ActivityCategoriesState], (state) =>
      state.categories.some((category) => category.id === id),
    );
  }

  ngxsOnInit(ctx: StateContext<ActivityCategoriesStateModel>): void {
    this.store
      .select(AuthState.userId)
      .pipe(filter((userId): userId is string => !!userId))
      .subscribe(() => ctx.dispatch(new FetchActivityCategories()));
  }

  @Action(FetchActivityCategories, { cancelUncompleted: true })
  fetchActivityCategories(
    ctx: StateContext<ActivityCategoriesStateModel>,
  ): Observable<ActivityCategory[] | null> {
    return this.activitiesGateway.getAllActivityCategories().pipe(
      handleWithRetryAndError(),
      take(1),
      tap((categories) => categories && ctx.patchState({ categories })),
    );
  }
}
